import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useRef, useEffect } from 'react';
import { cn } from '../../utils/cn';
import { useVirtualTable } from '../../hooks/use-virtual-table';
import { Checkbox } from '../checkbox';
// Helper function to determine if data is grouped
const isGroupedData = (data) => {
    return data.length > 0 && data[0] && typeof data[0] === 'object' && 'type' in data[0];
};
// Helper function for column alignment
const getAlignmentClass = (align) => {
    switch (align) {
        case 'center': return 'text-center';
        case 'right': return 'text-right';
        default: return 'text-left';
    }
};
export function VirtualTableCore({ data = [], columns = [], selectable = false, selectedRows = new Set(), onRowSelect, onSelectAll, actions = [], bulkActions = [], isCompact = false, loading = false, emptyMessage = "No data available", height = "400px", maxHeight, onRowClick, onRowDoubleClick, onGroupToggle, theme, columnVisibility, totalSelectableItems }) {
    // Refs for scroll synchronization
    const headerRef = useRef(null);
    // Filter visible columns
    const visibleColumns = useMemo(() => {
        if (!columnVisibility)
            return columns;
        return columns.filter(column => columnVisibility[column.key] !== false);
    }, [columns, columnVisibility]);
    // Calculate column width
    const getColumnWidth = (column) => {
        return column.defaultWidth || 150;
    };
    // Virtual table setup
    const { virtualItems, totalSize, containerProps, getRowProps, scrollElementRef } = useVirtualTable({
        data,
        columns: visibleColumns,
        isCompact,
        height,
        maxHeight,
        onRowClick,
        onRowDoubleClick,
        overscan: 5
    });
    // Scroll synchronization effect
    useEffect(() => {
        const bodyContainer = scrollElementRef.current;
        const headerContainer = headerRef.current;
        if (!bodyContainer || !headerContainer)
            return;
        const handleBodyScroll = () => {
            headerContainer.scrollLeft = bodyContainer.scrollLeft;
        };
        bodyContainer.addEventListener('scroll', handleBodyScroll);
        return () => bodyContainer.removeEventListener('scroll', handleBodyScroll);
    }, [scrollElementRef]);
    // Selection handlers - Fixed for grouped data
    // Get ALL data items (visible and hidden in collapsed groups)
    const allDataItems = useMemo(() => {
        // For non-grouped data, return as-is
        if (!isGroupedData(data)) {
            return data;
        }
        // For grouped data, we need to handle both visible and collapsed items
        // When a group is collapsed, its data items are not in the data array
        // So we need to get the total count from the parent or use visible items
        const visibleDataItems = data.filter(item => item.type === 'data').map(item => item.data);
        return visibleDataItems;
    }, [data]);
    // Get the actual total count of selectable items
    const totalSelectableCount = useMemo(() => {
        // Always prefer parent-provided total count when available
        // This ensures consistency across collapsed/expanded group states
        if (totalSelectableItems !== undefined && totalSelectableItems > 0) {
            return totalSelectableItems;
        }
        // For non-grouped data, use visible items count
        if (!isGroupedData(data) && allDataItems.length > 0) {
            return allDataItems.length;
        }
        // For grouped data without parent total, we can't reliably determine
        // the total count when groups are collapsed, so return 0 to disable
        // header checkbox functionality until parent provides totalSelectableItems
        return 0;
    }, [totalSelectableItems, allDataItems.length, data]);
    // Count how many items are actually selected
    const selectedCount = selectedRows.size;
    // Determine checkbox states
    const allSelected = selectedCount > 0 && totalSelectableCount > 0 && selectedCount === totalSelectableCount;
    const isIndeterminate = selectedCount > 0 && totalSelectableCount > 0 && selectedCount < totalSelectableCount;
    const handleSelectAll = (checked) => {
        // When selecting all, we need to pass either visible items or signal to select all
        // The parent component should handle selecting all items including those in collapsed groups
        if (checked && allDataItems.length === 0 && totalSelectableItems) {
            // All groups are collapsed, parent needs to handle this
            // Pass empty array with a flag or let parent handle based on checked state
            onSelectAll?.(checked, []);
        }
        else {
            // Pass visible data items
            onSelectAll?.(checked, allDataItems);
        }
    };
    const handleRowSelect = (item, event) => {
        onRowSelect?.(item, event.target.checked);
    };
    // Render cell content - SINGLE FUNCTION FOR ALL ROW TYPES
    const renderCell = (column, item, rowType, idx) => {
        let content = '';
        // Only add border for header and data rows, not for group or summary rows
        const shouldHaveBorder = rowType === 'header' || rowType === 'data';
        let cellClasses = cn("truncate", shouldHaveBorder && "border-r border-theme-border/30", isCompact ? "text-sm px-3 py-2" : "text-base px-4 py-3", getAlignmentClass(column.align));
        // Determine content based on row type
        switch (rowType) {
            case 'header':
                content = column.label;
                cellClasses = cn(cellClasses, "font-semibold text-white/90 bg-transparent");
                break;
            case 'group':
                if (idx === 0) {
                    const groupedItem = item;
                    const indent = (groupedItem.level || 0) * 20;
                    // Calculate selected count for this group (if we have selection info)
                    const selectedCount = groupedItem.selectedCount || 0;
                    const totalCount = groupedItem.childCount || 0;
                    // Show selection info for collapsed groups
                    let selectionInfo = '';
                    if (selectable && !groupedItem.isExpanded && selectedCount > 0) {
                        selectionInfo = ` (${selectedCount}/${totalCount} selected)`;
                    }
                    else if (selectable && !groupedItem.isExpanded) {
                        selectionInfo = ` (0/${totalCount} selected)`;
                    }
                    else {
                        selectionInfo = ` (${totalCount} items)`;
                    }
                    content = `${groupedItem.isExpanded ? '▼' : '▶'} ${groupedItem.groupLabel || groupedItem.groupValue}${selectionInfo}`;
                    cellClasses = cn(cellClasses, "font-semibold text-theme-foreground bg-theme-secondary/30 text-left");
                    // Calculate total width for spanning all columns for proper horizontal scroll
                    const totalWidth = visibleColumns.reduce((sum, col) => sum + getColumnWidth(col), 0);
                    return (_jsx("div", { className: cellClasses, style: {
                            width: `${totalWidth}px`,
                            minWidth: `${totalWidth}px`,
                            paddingLeft: `${(isCompact ? 12 : 16) + indent}px`,
                            position: 'absolute',
                            left: 0,
                            right: 0,
                            zIndex: 1,
                            textAlign: 'left'
                        }, onClick: () => onGroupToggle?.(groupedItem.groupKey), children: content }, column.key));
                }
                else {
                    // Return empty div for other columns since first column spans all
                    return (_jsx("div", { className: cn(cellClasses, "bg-theme-secondary/30"), style: {
                            width: `${getColumnWidth(column)}px`,
                            minWidth: column.minWidth || '80px',
                            maxWidth: column.maxWidth
                        } }, column.key));
                }
            case 'group-summary':
                const groupSummaryItem = item;
                const summaryValue = groupSummaryItem.summary?.[column.key];
                content = summaryValue !== undefined
                    ? (column.render ? column.render(summaryValue, groupSummaryItem.summary) : String(summaryValue))
                    : '';
                cellClasses = cn(cellClasses, "font-semibold text-theme-accent bg-theme-accent/10");
                break;
            case 'summary':
                const grandTotalItem = item;
                const totalValue = grandTotalItem.summary?.[column.key];
                content = totalValue !== undefined
                    ? (column.render ? column.render(totalValue, grandTotalItem.summary) : String(totalValue))
                    : '';
                cellClasses = cn(cellClasses, "font-bold text-theme-foreground bg-gradient-to-r from-theme-accent/30 to-theme-accent/20");
                break;
            case 'data':
            default:
                const dataItem = isGroupedData([item]) ? item.data : item;
                const value = dataItem?.[column.key];
                content = column.render ? column.render(value, dataItem) : String(value || '');
                cellClasses = cn(cellClasses, "text-theme-foreground hover:bg-theme-secondary/50");
                break;
        }
        return (_jsx("div", { className: cellClasses, style: {
                width: `${getColumnWidth(column)}px`,
                minWidth: column.minWidth || '80px',
                maxWidth: column.maxWidth
            }, title: typeof content === 'string' ? content : undefined, children: content }, column.key));
    };
    // Render checkbox cell - SINGLE FUNCTION FOR ALL ROW TYPES
    const renderCheckbox = (item, rowType) => {
        if (!selectable)
            return null;
        const shouldHaveBorder = rowType === 'header' || rowType === 'data';
        const cellClasses = cn("flex items-center justify-center", isCompact ? "w-8 px-2 py-2" : "w-10 px-3 py-3");
        switch (rowType) {
            case 'header':
                return (_jsx("div", { className: cn(cellClasses, "bg-transparent border-r border-primary-500/30 flex items-center justify-center"), children: _jsx(Checkbox, { size: isCompact ? 'sm' : 'md', checked: allSelected, indeterminate: isIndeterminate, onChange: (e) => handleSelectAll(e.target.checked), showIcon: true, animateCheck: false }) }));
            case 'data':
                const dataItem = isGroupedData([item]) ? item.data : item;
                const isSelected = selectedRows.has(String(dataItem?.id));
                return (_jsx("div", { className: cn(cellClasses, "border-r border-theme-border/30 hover:bg-theme-secondary/50 flex items-center justify-center"), children: _jsx(Checkbox, { size: isCompact ? 'sm' : 'md', checked: isSelected, onChange: (e) => handleRowSelect(dataItem, e), onClick: (e) => e.stopPropagation(), showIcon: true, animateCheck: false }) }));
            default:
                // Empty space for group headers, summaries, etc.
                return _jsx("div", { className: cn(cellClasses, shouldHaveBorder && "border-r border-theme-border/30") });
        }
    };
    // Render actions cell - SINGLE FUNCTION FOR ALL ROW TYPES
    const renderActions = (item, rowType) => {
        if (actions.length === 0)
            return null;
        const shouldHaveBorder = rowType === 'header' || rowType === 'data';
        const cellClasses = cn("flex items-center justify-center gap-1", isCompact ? "w-20 px-2 py-2" : "w-24 px-3 py-3");
        if (rowType === 'header') {
            return _jsx("div", { className: cn(cellClasses, "bg-transparent border-r border-primary-500/30 text-white/90 font-semibold"), children: "Actions" });
        }
        if (rowType === 'data') {
            const dataItem = isGroupedData([item]) ? item.data : item;
            return (_jsx("div", { className: cn(cellClasses, "border-r border-theme-border/30 hover:bg-theme-secondary/50"), children: actions.slice(0, 2).map((action) => (_jsx("button", { onClick: () => action.onClick(dataItem), className: "p-1 text-theme-muted hover:text-theme-foreground transition-colors", title: action.label, children: action.icon }, action.key))) }));
        }
        // Empty space for other row types
        return _jsx("div", { className: cn(cellClasses, shouldHaveBorder && "border-r border-theme-border/30") });
    };
    // Handle empty state
    if (data.length === 0 && !loading) {
        return (_jsx("div", { className: cn("flex items-center justify-center py-12", "text-theme-muted", isCompact ? "text-sm" : "text-base"), children: emptyMessage }));
    }
    // Calculate minimum table width for horizontal scrolling
    const minTableWidth = useMemo(() => {
        const columnsWidth = visibleColumns.reduce((sum, col) => sum + getColumnWidth(col), 0);
        const checkboxWidth = selectable ? (isCompact ? 32 : 40) : 0;
        const actionsWidth = actions.length > 0 ? (isCompact ? 80 : 96) : 0;
        return columnsWidth + checkboxWidth + actionsWidth;
    }, [visibleColumns, selectable, actions.length, isCompact]);
    return (_jsxs("div", { className: "relative", children: [_jsx("div", { ref: headerRef, className: cn("sticky top-0 z-10 overflow-x-auto scrollbar-hide", "bg-primary-600 dark:bg-primary-700 text-white shadow-sm border-b-2 border-primary-700 dark:border-primary-600"), children: _jsxs("div", { className: cn("flex items-center", isCompact ? "h-10" : "h-12"), style: { minWidth: `${minTableWidth}px` }, children: [renderCheckbox(null, 'header'), visibleColumns.map((column, idx) => renderCell(column, null, 'header', idx)), renderActions(null, 'header')] }) }), _jsx("div", { ...containerProps, className: cn(containerProps.className, "virtual-table-scrollbar", loading && "opacity-50 pointer-events-none"), style: {
                    ...containerProps.style,
                    scrollbarWidth: 'thin',
                    scrollbarColor: 'var(--theme-border) var(--theme-secondary)'
                }, children: _jsx("div", { style: { height: `${totalSize}px`, position: 'relative', minWidth: `${minTableWidth}px` }, children: virtualItems.map((virtualItem) => {
                        const item = data[virtualItem.index];
                        if (!item)
                            return null;
                        const { key, props: rowProps } = getRowProps(virtualItem, item);
                        // Determine row type
                        let rowType = 'data';
                        if (isGroupedData(data)) {
                            const groupedItem = item;
                            rowType = groupedItem.type;
                        }
                        return (_jsxs("div", { ...rowProps, className: cn(rowProps.className, "flex items-center border-b border-theme-border", rowType === 'data' && "hover:bg-theme-secondary/30 cursor-pointer", rowType === 'group' && "cursor-pointer", rowType === 'summary' && "border-t-4 border-theme-accent", loading && "pointer-events-none"), style: {
                                ...rowProps.style,
                                minWidth: `${minTableWidth}px`
                            }, children: [renderCheckbox(item, rowType), visibleColumns.map((column, idx) => renderCell(column, item, rowType, idx)), renderActions(item, rowType)] }, key));
                    }) }) }), loading && (_jsx("div", { className: "absolute inset-0 flex items-center justify-center bg-theme-primary/50 backdrop-blur-sm", children: _jsxs("div", { className: "flex items-center gap-2", children: [_jsx("div", { className: "animate-spin rounded-full h-4 w-4 border-2 border-theme-accent border-t-transparent" }), _jsx("span", { className: cn("text-theme-foreground", isCompact ? "text-sm" : "text-base"), children: "Loading..." })] }) }))] }));
}
